import { Injectable } from '@angular/core';
import { LogService } from '@core/services/core/log.service';
import { SecureStoreService } from '@core/services/core/storage/secure.service';
import { PaymentPlanState } from '@models/payment-plan';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { finalize, first, map, switchMap, tap } from 'rxjs/operators';
import { getDaysInMonth, isEmpty } from '../../utils/utils';
import { PoliciesHelperService } from '../core/policies-helper.service';
import { UiHelperService } from '../core/ui-helper.service';
import { PaymentPlanAccess, PaymentPlanService } from '../http-services/payment-plan.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentPlanContextService {
  private readonly initialState = {
    paymentMonthDay: new Date().getDate(),
    daysInCurrentMonth: Array(getDaysInMonth())
      .fill('')
      .map((_, i) => i + 1)
  };
  private stateSource = new BehaviorSubject<PaymentPlanState>(
    this.initialState
  );
  private stateUpdated = false;

  constructor(
    private paymentPlanService: PaymentPlanService,
    private policiesHelper: PoliciesHelperService,
    protected log: LogService,
    protected secure: SecureStoreService,
    private ui: UiHelperService
  ) {}

  get state() {
    return this.stateSource.asObservable();
  }

  private setState(state: PaymentPlanState) {
    this.stateUpdated = true;
    this.stateSource.next({ ...this.stateSource.getValue(), ...state });
  }

  setInitialState() {
    this.stateSource.next(this.initialState);
  }

  updateState(state: PaymentPlanState) {
    this.setState(state);
  }

  clear() {
    this.stateSource.next({});
  }

  getAccessAndSusceptiblePolicies() {
    if (!isEmpty(this.stateSource.getValue().access)) {
      return of(this.stateSource.getValue());
    }

    return forkJoin([
      this.paymentPlanService.getPaymentPlanAccess(),
      this.ui.presentLoading('payment-access')
    ]).pipe(
      tap(([accessData]) => {
        this.stateSource.next({
          ...this.stateSource.getValue(),
          ...accessData
        });
      }),
      map(() => this.stateSource.getValue()),
      first(),
      finalize(() => this.ui.dismissLoading('payment-access'))
    );
  }

  getCampaign() {
    if (!isEmpty(this.stateSource.getValue().campaign)) {
      return of(this.stateSource.getValue());
    }

    return this.getAccessAndSusceptiblePolicies().pipe(
      switchMap(({ susceptiblePolicyNumbers }) =>
        this.calculateCampaign(susceptiblePolicyNumbers)
      ),
      tap(campaign => {
        this.stateSource.next({ ...this.stateSource.getValue(), campaign });
      }),
      map(() => this.stateSource.getValue()),
      first()
    );
  }

  hasState() { return this.stateUpdated; }

  checkPaymentPlanBusiness(){
   return this.getAccessAndSusceptiblePolicies().pipe(
      map(({access}) => {
        switch (access) {
          case PaymentPlanAccess.SUSCEPTIBLE:
          case PaymentPlanAccess.NORMAL:
            this.paymentPlanService.updatePaymentPlanAccessOnHomeState(true);
            break;
          case PaymentPlanAccess.NOT_SUSCEPTIBLE:
            this.paymentPlanService.updatePaymentPlanAccessOnHomeState(false);
            break;
          default:
            this.paymentPlanService.updatePaymentPlanAccessOnHomeState(false);
            break;
        }
      }),
      );
  }

  private calculateCampaign(susceptiblePolicyNumbers: string[]) {
    const policiesAreLife = 'CallMe_MET_PlanPagV';
    const policiesAreHealth = 'CallMe_MET_PlanPagS';
    const policiesMixed = 'CallMe_MET_PlanPag';

    return this.policiesHelper
      .getAllPoliciesById()
      .then(policiesById => {
        const policies = Object.values(policiesById).filter(
          ({ policyNumber }) => susceptiblePolicyNumbers.includes(policyNumber)
        );

        if (policies.every(({ policyBaseType }) => policyBaseType === 'life')) {
          return policiesAreLife;
        }
        if (
          policies.every(({ policyBaseType }) => policyBaseType === 'health')
        ) {
          return policiesAreHealth;
        }

        return policiesMixed;
      })
      .catch(() => policiesMixed);
  }
}
